import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).substring(0, 10);
  return d.toLocaleDateString();
};

const formatTime = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.length >= 5 ? str.substring(0, 5) : str;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const ViewShiftBookingsModal = ({ isOpen, onClose, slot, onChanged }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [bookings, setBookings] = useState([]);
  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const shiftSlotId = slot?.shiftSlotId || slot?.ShiftSlot_ID;
  const serviceCity = (slot?.serviceCity || slot?.ServiceCity || "").trim();
  const zoneId = slot?.zoneId || slot?.Zone_ID || null;
  const zoneName = slot?.zoneName || slot?.ZoneName || "";
  const slotDate = slot?.shiftDate || slot?.ShiftDate;
  const slotStart = formatTime(slot?.startTime || slot?.StartTime);
  const slotEnd = formatTime(slot?.endTime || slot?.EndTime);
  const slotTimeLabel = slotStart && slotEnd ? `${slotStart} – ${slotEnd}` : "";
  const maxRiders = Number(slot?.maxRiders ?? slot?.MaxRiders ?? 0);

  const activeBookings = useMemo(
    () => bookings.filter((b) => String(b.status || b.Status || "").toLowerCase() === "booked"),
    [bookings]
  );
  const cancelledBookings = useMemo(
    () => bookings.filter((b) => String(b.status || b.Status || "").toLowerCase() !== "booked"),
    [bookings]
  );
  const visibleBookings = showCancelled ? bookings : activeBookings;

  const bookedCount = loading
    ? (slot?.bookedCount ?? slot?.BookedCount ?? 0)
    : activeBookings.length;
  const seatsLeft = Math.max(0, maxRiders - bookedCount);
  const isFull = maxRiders > 0 && bookedCount >= maxRiders;
  const fillPct = maxRiders > 0 ? Math.min(100, Math.round((bookedCount / maxRiders) * 100)) : 0;

  const selectStyles = useMemo(
    () => ({
      ...dropdownStyles,
      control: (provided, state) => ({
        ...dropdownStyles.control(provided, state),
        minHeight: "44px",
        height: "44px",
        borderRadius: "10px",
        background: isDarkMode ? "#2d3436" : "#F9F9F9",
        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
        boxShadow: "none",
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 10150,
      }),
      menu: (provided) => ({
        ...dropdownStyles.menu(provided),
        zIndex: 10150,
      }),
    }),
    [dropdownStyles, isDarkMode]
  );

  const panelStyle = useMemo(
    () => ({
      borderRadius: "12px",
      border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eceff1",
      background: isDarkMode ? "rgba(45, 52, 54, 0.55)" : "#fafbfc",
      padding: "16px",
    }),
    [isDarkMode]
  );

  const fetchBookings = useCallback(async () => {
    if (!shiftSlotId) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RIDER_SHIFT_SLOT_BOOKINGS, {
        params: { ShiftSlot_ID: shiftSlotId },
      });
      if (response.data.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setBookings(rows.filter((b) => (b.shiftSlotId || b.ShiftSlot_ID) === shiftSlotId));
      } else {
        showErrorToast(response.data.message || "Failed to load bookings");
      }
    } catch {
      showErrorToast("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [shiftSlotId]);

  const fetchRiders = useCallback(async () => {
    if (!zoneId && !serviceCity) {
      setRiders([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_MARKETPLACE_RIDERS, {
        params: {
          ApprovalStatus: "Approved",
          PageNumber: 1,
          PageSize: 500,
          SortBy: "FirstName",
          SortOrder: "ASC",
        },
      });
      if (response.data.status) {
        const all = Array.isArray(response.data.data) ? response.data.data : [];
        const eligible = all.filter((r) => {
          const riderZoneIds = Array.isArray(r.zoneIds)
            ? r.zoneIds
            : Array.isArray(r.zones)
              ? r.zones.map((z) => z.zoneId || z.Zone_ID).filter(Boolean)
              : [];
          if (zoneId) return riderZoneIds.includes(zoneId);
          const cityLower = serviceCity.toLowerCase();
          const riderCity = (r.serviceCity || "").trim().toLowerCase();
          return riderCity === cityLower;
        });
        setRiders(eligible);
      }
    } catch {
      setRiders([]);
    }
  }, [zoneId, serviceCity]);

  useEffect(() => {
    if (isOpen && shiftSlotId) {
      setBookings([]);
      setShowCancelled(false);
      fetchBookings();
      fetchRiders();
      setSelectedRider(null);
    } else if (!isOpen) {
      setBookings([]);
      setRiders([]);
      setSelectedRider(null);
    }
  }, [isOpen, shiftSlotId, fetchBookings, fetchRiders]);

  const bookedRiderIds = useMemo(
    () => new Set(
      activeBookings
        .map((b) => b.marketplaceRiderId || b.MarketplaceRider_ID)
        .filter(Boolean)
    ),
    [activeBookings]
  );

  const riderOptions = useMemo(
    () =>
      riders
        .filter((r) => !bookedRiderIds.has(r.marketplaceRiderId || r.marketplaceRider_ID))
        .map((r) => {
          const id = r.marketplaceRiderId || r.marketplaceRider_ID;
          const name = r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim();
          return {
            value: String(id),
            label: `${name} — ${r.phone || "no phone"}`,
          };
        }),
    [riders, bookedRiderIds]
  );

  const handleAssign = async () => {
    const riderId = parseInt(selectedRider?.value, 10);
    if (!riderId || !shiftSlotId) {
      showErrorToast("Select a rider to assign");
      return;
    }
    try {
      setAssigning(true);
      const response = await axiosInstance.post(APIs.POST_BOOK_RIDER_SHIFT_SLOT, {
        shiftSlot_ID: shiftSlotId,
        marketplaceRider_ID: riderId,
      });
      if (response.data.status) {
        showSuccessToast("Rider assigned to shift");
        setSelectedRider(null);
        await fetchBookings();
        onChanged?.();
      } else {
        showErrorToast(response.data.message || "Failed to assign rider");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to assign rider");
    } finally {
      setAssigning(false);
    }
  };

  const handleCancel = async (booking) => {
    const bookingId = booking.bookingId || booking.Booking_ID;
    const riderId = booking.marketplaceRiderId || booking.MarketplaceRider_ID;
    if (!bookingId || !riderId) return;
    if (!window.confirm("Cancel this booking?")) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_CANCEL_RIDER_SHIFT_BOOKING, {
        booking_ID: bookingId,
        marketplaceRider_ID: riderId,
      });
      if (response.data.status) {
        showSuccessToast("Booking cancelled");
        await fetchBookings();
        onChanged?.();
      } else {
        showErrorToast(response.data.message || "Failed to cancel booking");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to cancel booking");
    } finally {
      setLoading(false);
    }
  };

  const title = slot
    ? `${zoneName || serviceCity || "Shift"}${zoneName && serviceCity ? ` · ${serviceCity}` : ""}`
    : "Shift bookings";

  const subtitle = slot
    ? `${formatDate(slotDate)}${slotTimeLabel ? `  ·  ${slotTimeLabel}` : ""}`
    : "";

  const emptyRiderPlaceholder =
    riders.length === 0
      ? `No approved riders for ${zoneName || serviceCity || "this zone"}`
      : "All eligible riders are already booked";

  const statBox = (label, value, emphasize = false) => (
    <div
      className="text-center px-3 py-2"
      style={{
        flex: 1,
        minWidth: 90,
        borderRadius: 10,
        background: isDarkMode ? "rgba(0,0,0,0.2)" : "#fff",
        border: isDarkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid #eee",
      }}
    >
      <div className="small text-muted text-uppercase" style={{ letterSpacing: "0.04em", fontSize: 11 }}>
        {label}
      </div>
      <div
        className="fw-bold"
        style={{
          fontSize: "1.25rem",
          color: emphasize ? themeColor : undefined,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <CustomModal
      title={title}
      subtitle={subtitle}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      modalSize="modal-lg"
      maxHeight="90vh"
      minHeight="320px"
      zIndex={10050}
    >
      {loading && <Preloader />}

      {/* Capacity overview */}
      <div className="mb-3" style={panelStyle}>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {statBox("Booked", bookedCount, true)}
          {statBox("Seats left", maxRiders ? seatsLeft : "—", !isFull && seatsLeft > 0)}
          {statBox("Capacity", maxRiders || "—")}
        </div>
        {maxRiders > 0 && (
          <div>
            <div
              className="rounded-pill overflow-hidden"
              style={{ height: 8, background: isDarkMode ? "rgba(255,255,255,0.1)" : "#e9ecef" }}
            >
              <div
                style={{
                  width: `${fillPct}%`,
                  height: "100%",
                  background: isFull ? "#ffc107" : themeColor,
                  transition: "width 0.25s ease",
                }}
              />
            </div>
            <div className="d-flex justify-content-between mt-1">
              <small className="text-muted">{fillPct}% filled</small>
              {isFull && <small className="text-warning fw-semibold">Slot is full</small>}
            </div>
          </div>
        )}
      </div>

      {/* Assign */}
      {!isFull ? (
        <div className="mb-3" style={panelStyle}>
          <div className="fw-semibold mb-2">Assign a rider</div>
          <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-start">
            <div className="flex-grow-1">
              <Select
                value={selectedRider}
                onChange={setSelectedRider}
                options={riderOptions}
                styles={selectStyles}
                placeholder={riderOptions.length === 0 ? emptyRiderPlaceholder : "Search rider by name or phone…"}
                isDisabled={assigning || riderOptions.length === 0}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <small className="text-muted d-block mt-1">
                Approved riders for zone &quot;{zoneName || serviceCity}&quot; only.
              </small>
            </div>
            <button
              type="button"
              className="btn flex-shrink-0"
              onClick={handleAssign}
              disabled={assigning || !selectedRider}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                height: "44px",
                minWidth: 150,
                fontWeight: 600,
                boxShadow: selectedRider ? `0 2px 8px ${themeColor}40` : "none",
                opacity: assigning || !selectedRider ? 0.65 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {assigning ? "Assigning…" : "Assign"}
            </button>
          </div>
          {riderOptions.length === 0 && !loading && (
            <div className="alert alert-light border mt-3 mb-0 py-2 small">
              {riders.length === 0
                ? "No eligible riders found for this zone. Approve riders or assign them to this zone first."
                : "Every eligible rider is already on this shift."}
            </div>
          )}
        </div>
      ) : (
        <div
          className="mb-3 px-3 py-2 rounded d-flex align-items-center gap-2"
          style={{
            background: "rgba(255, 193, 7, 0.12)",
            border: "1px solid rgba(255, 193, 7, 0.35)",
          }}
        >
          <i className="fa fa-info-circle text-warning" />
          <span className="small mb-0">
            This shift is full. Cancel a booking below to free a seat.
          </span>
        </div>
      )}

      {/* List header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div className="d-flex align-items-center gap-2">
          <span style={{ width: 4, height: 16, borderRadius: 4, background: themeColor }} />
          <h6 className="mb-0 fw-semibold">
            {showCancelled ? "All bookings" : "Booked riders"}
            <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.85rem" }}>
              ({visibleBookings.length})
            </span>
          </h6>
        </div>
        {cancelledBookings.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-link text-decoration-none p-0"
            onClick={() => setShowCancelled((v) => !v)}
            style={{ color: themeColor, fontWeight: 600 }}
          >
            {showCancelled ? "Hide cancelled" : `Show cancelled (${cancelledBookings.length})`}
          </button>
        )}
      </div>

      {visibleBookings.length === 0 ? (
        <div
          className="text-center py-5 px-3"
          style={{
            borderRadius: 12,
            border: isDarkMode ? "1px dashed rgba(255,255,255,0.15)" : "1px dashed #d0d5dd",
            background: isDarkMode ? "rgba(0,0,0,0.15)" : "#fcfcfd",
          }}
        >
          <div className="mb-2" style={{ fontSize: "1.75rem", opacity: 0.35 }}>
            <i className="fa fa-user-plus" />
          </div>
          <div className="fw-semibold mb-1">No riders booked yet</div>
          <div className="text-muted small">
            {isFull
              ? "Capacity shows full from slot data, but no active bookings were returned."
              : "Pick a rider above and click Assign."}
          </div>
        </div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: 340, overflowY: "auto" }}>
          <table className="table table-hover align-middle mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              }}
            >
              <tr>
                <th className="pt-2 pb-2">Rider</th>
                <th className="pt-2 pb-2">Assigned</th>
                <th className="pt-2 pb-2 text-center">Rating</th>
                {showCancelled && <th className="pt-2 pb-2 text-center">Status</th>}
                <th className="pt-2 pb-2 text-end" style={{ width: 100 }}> </th>
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b) => {
                const status = b.status || b.Status;
                const isBooked = String(status || "").toLowerCase() === "booked";
                const riderId = b.marketplaceRiderId || b.MarketplaceRider_ID;
                const name = b.riderName || `${b.firstName || ""} ${b.lastName || ""}`.trim() || "—";
                return (
                  <tr key={b.bookingId || b.Booking_ID} style={{ opacity: isBooked ? 1 : 0.65 }}>
                    <td>
                      <div className="fw-semibold">{name}</div>
                      <div className="small text-muted">{b.phone || "—"}</div>
                    </td>
                    <td className="text-nowrap small">
                      {formatDateTime(b.bookedAt || b.BookedAt)}
                    </td>
                    <td className="text-center">
                      {b.rating != null ? Number(b.rating).toFixed(1) : "—"}
                    </td>
                    {showCancelled && (
                      <td className="text-center">
                        <span className={`badge ${isBooked ? "bg-success" : "bg-secondary"}`}>
                          {status || "—"}
                        </span>
                      </td>
                    )}
                    <td className="text-end">
                      {isBooked && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => handleCancel({ ...b, marketplaceRiderId: riderId })}
                          title="Cancel booking"
                          style={{
                            border: "1px solid #dc3545",
                            color: "#dc3545",
                            background: "transparent",
                            borderRadius: "6px",
                            fontWeight: 600,
                            padding: "4px 10px",
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CustomModal>
  );
};

export default ViewShiftBookingsModal;
